import React, { useState } from "react";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Calculator } from "lucide-react";

export default function CorporateGifting() {
  const [form, setForm] = useState({ company: "", contact_name: "", email: "", phone: "", gst: "", billing_address: "", product_type: "shirt", quantity: 50, customisation: "", budget: 0, delivery_by: "", notes: "" });
  const [quote, setQuote] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.company || !form.email) return toast.error("Company & email required");
    try {
      const { data } = await api.post("/corporate/quote", { ...form, quantity: Number(form.quantity), budget: Number(form.budget) });
      setQuote(data); toast.success(`Quote ${data.id} generated`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <main data-testid="corporate-page">
      <section className="relative h-[55vh] overflow-hidden bg-black text-white">
        <img src="https://images.unsplash.com/photo-1591944489410-16ec1074a18e?crop=entropy&cs=srgb&fm=jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="relative z-10 sq-container h-full flex flex-col justify-end pb-12">
          <div className="sq-eyebrow text-white/70">B2B · Bulk · Gifting</div>
          <h1 className="font-display text-5xl md:text-7xl mt-3 leading-[0.95]">Corporate Gifting & Uniforms.</h1>
          <p className="mt-4 max-w-xl text-white/80">Branded shirts, embroidered blazers, gifting sets — with auto-quote, GST invoicing and a dedicated account manager.</p>
        </div>
      </section>
      <section className="sq-container py-16 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="border border-black/10 p-8">
          <div className="sq-eyebrow flex items-center gap-2"><Calculator size={12} /> Instant Quote</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {[["company", "Company"], ["contact_name", "Contact Name"], ["email", "Email"], ["phone", "Phone"], ["gst", "GST Number"], ["delivery_by", "Delivery by (YYYY-MM-DD)"]].map(([k, l]) => (
              <label key={k} className="block">
                <span className="sq-eyebrow text-[10px]">{l}</span>
                <input data-testid={`corp-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} className="w-full bg-transparent border-b border-black/30 focus:border-squire-jeans outline-none py-2 text-sm" />
              </label>
            ))}
            <label className="block md:col-span-2">
              <span className="sq-eyebrow text-[10px]">Billing Address</span>
              <input data-testid="corp-billing" value={form.billing_address} onChange={(e) => set("billing_address", e.target.value)} className="w-full bg-transparent border-b border-black/30 outline-none py-2 text-sm" />
            </label>
            <label className="block"><span className="sq-eyebrow text-[10px]">Product Type</span>
              <select data-testid="corp-product-type" value={form.product_type} onChange={(e) => set("product_type", e.target.value)} className="w-full bg-transparent border-b border-black/30 outline-none py-2 text-sm">
                {["shirt", "blazer", "suit", "uniform", "gifting"].map((p) => <option key={p}>{p}</option>)}
              </select>
            </label>
            <label className="block"><span className="sq-eyebrow text-[10px]">Quantity</span>
              <input data-testid="corp-qty" type="number" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className="w-full bg-transparent border-b border-black/30 outline-none py-2 text-sm" />
            </label>
            <label className="block md:col-span-2">
              <span className="sq-eyebrow text-[10px]">Customisation (logo, monogram)</span>
              <input data-testid="corp-customisation" value={form.customisation} onChange={(e) => set("customisation", e.target.value)} className="w-full bg-transparent border-b border-black/30 outline-none py-2 text-sm" />
            </label>
            <label className="block md:col-span-2">
              <span className="sq-eyebrow text-[10px]">Brief</span>
              <textarea data-testid="corp-notes" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="w-full bg-transparent border border-black/10 focus:border-squire-jeans outline-none p-2 text-sm resize-none" />
            </label>
          </div>
          <button data-testid="corp-submit" onClick={submit} className="sq-btn-primary mt-6"><Building2 size={14} /> Generate Quote</button>
        </div>

        <div>
          {!quote ? (
            <div className="border border-dashed border-black/20 p-8 text-center" data-testid="corp-empty">
              <div className="sq-eyebrow text-squire-jeans">Volume Tiers</div>
              <ul className="mt-4 text-sm space-y-2">
                <li>25+ pieces → 8% off</li>
                <li>50+ pieces → 12% off</li>
                <li>100+ pieces → 18% off</li>
                <li className="text-squire-jeans">Logo embroidery: +₹250 per piece</li>
              </ul>
              <p className="text-xs text-[#4B5563] mt-6">Fill the form & click Generate Quote. We'll assign an account manager.</p>
            </div>
          ) : (
            <div className="border border-squire-jeans p-8 bg-[#F5F5F5]" data-testid="corp-quote">
              <div className="sq-eyebrow text-squire-jeans">Auto-generated Quote</div>
              <div className="font-display text-2xl mt-1">{quote.id}</div>
              <div className="mt-6 space-y-2 text-sm">
                <Row label={`Unit (${quote.product_type})`} v={formatINR(quote.unit_price)} />
                <Row label={`× ${quote.quantity}`} v={formatINR(quote.subtotal)} />
                <Row label={`Bulk discount (${quote.discount_pct}%)`} v={`−${formatINR(quote.discount)}`} />
                <Row label="GST (5%)" v={formatINR(quote.gst_amount)} />
                <div className="border-t border-black/10 pt-2"><Row label="Total" v={formatINR(quote.total)} strong /></div>
              </div>
              <p className="text-xs text-[#4B5563] mt-6">An account manager will reach out within 2 business hours. Reference: {quote.id}</p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
function Row({ label, v, strong }) {
  return <div className={`flex justify-between ${strong ? "font-display text-xl" : ""}`}><span>{label}</span><span>{v}</span></div>;
}
